import { createPortalioClient } from './client';
import { createPortalOutboundPreviewService } from './outboundPreviewService';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type {
  PortalOutboundCreateInput,
  PortalOutboundCreateResult,
  PortalOutboundCreatedArea,
  PortalOutboundPortalUser,
  PortalOutboundPreview,
  PortalOutboundPreviewItem,
} from '@/types/portalOutbound';

interface PortalAreaResponse {
  id?: number;
  name?: string;
  areaName?: string;
  options?: Array<{ id?: number; status?: string; items?: unknown[] }>;
}

function compactText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function makeUniqueAreaNames(preview: PortalOutboundPreview): Map<string, string> {
  const rawNames = new Map<string, string>();
  const counts = new Map<string, number>();

  for (const area of preview.areas) {
    const name = compactText(area.name) || compactText(area.floorPlanName) || 'vfloow Area';
    rawNames.set(area.key, name);
    counts.set(name.toLowerCase(), (counts.get(name.toLowerCase()) || 0) + 1);
  }

  const namesByAreaKey = new Map<string, string>();
  const used = new Set<string>();

  for (const area of preview.areas) {
    const rawName = rawNames.get(area.key) || 'vfloow Area';
    const needsFloorPrefix = (counts.get(rawName.toLowerCase()) || 0) > 1;
    let candidate = needsFloorPrefix ? `${area.floorPlanName} - ${rawName}` : rawName;
    let suffix = 2;

    while (used.has(candidate.toLowerCase())) {
      candidate = `${needsFloorPrefix ? `${area.floorPlanName} - ${rawName}` : rawName} ${suffix}`;
      suffix += 1;
    }

    used.add(candidate.toLowerCase());
    namesByAreaKey.set(area.key, candidate);
  }

  return namesByAreaKey;
}

function getPortalIdentity(item: PortalOutboundPreviewItem): string {
  const product = item.product;
  if (product?.referencedItemId) return `ReferencedItemId ${product.referencedItemId}`;
  if (product?.portalItemId) return `PortalItemId ${product.portalItemId}`;
  if (product?.portalItemIdentifier) return `Portal identity ${product.portalItemIdentifier}`;
  return 'Portal identity stored in vfloow';
}

function getPartDetail(item: PortalOutboundPreviewItem): string | null {
  const details = [item.product?.brand, item.product?.model, item.product?.partNumber]
    .map(compactText)
    .filter(Boolean);

  return details.length > 0 ? details.join(' / ') : null;
}

function findCreatedArea(response: any, areaName: string): PortalAreaResponse | null {
  const areas = Array.isArray(response?.areas) ? response.areas : [];
  const normalizedName = areaName.trim().toLowerCase();

  const exactMatch = areas.find((area: PortalAreaResponse) => {
    const name = compactText(area.name) || compactText(area.areaName);
    return name?.toLowerCase() === normalizedName;
  });

  return exactMatch || areas[areas.length - 1] || null;
}

function findProposalArea(
  proposal: any,
  areaName: string,
  areaId?: number,
): PortalAreaResponse | null {
  const areas = Array.isArray(proposal?.areas) ? proposal.areas : [];
  const normalizedName = areaName.trim().toLowerCase();

  if (Number.isFinite(areaId)) {
    const idMatch = areas.find((area: PortalAreaResponse) => Number(area.id) === areaId);
    if (idMatch) return idMatch;
  }

  return (
    areas.find((area: PortalAreaResponse) => {
      const name = compactText(area.name) || compactText(area.areaName);
      return name?.toLowerCase() === normalizedName;
    }) || null
  );
}

function getDefaultOptionId(area: PortalAreaResponse | null): number | null {
  const options = Array.isArray(area?.options) ? area.options : [];
  const draft = options.find(
    option => option.status?.toLowerCase() === 'draft' && Number.isFinite(Number(option.id)),
  );
  const fallback = options.find(option => Number.isFinite(Number(option.id)));
  const optionId = draft?.id || fallback?.id;
  return Number.isFinite(Number(optionId)) ? Number(optionId) : null;
}

function countProposalLineItems(proposal: any): number {
  const areas = Array.isArray(proposal?.areas) ? proposal.areas : [];

  return areas.reduce((areaTotal: number, area: PortalAreaResponse) => {
    const options = Array.isArray(area.options) ? area.options : [];
    const optionTotal = options.reduce((total: number, option) => {
      return total + (Array.isArray(option.items) ? option.items.length : 0);
    }, 0);
    return areaTotal + optionTotal;
  }, 0);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message || 'Unknown error');
  }
  return String(error || 'Unknown error');
}

function wait(ms: number) {
  return new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

function buildAiBuilderContent(
  preview: PortalOutboundPreview,
  proposalName: string,
  items: PortalOutboundPreviewItem[],
  createdAreas: PortalOutboundCreatedArea[],
): string {
  const createdAreaByKey = new Map(createdAreas.map(area => [area.areaKey, area]));
  const itemsByAreaKey = new Map<string, PortalOutboundPreviewItem[]>();

  for (const item of items) {
    const areaItems = itemsByAreaKey.get(item.areaKey) || [];
    areaItems.push(item);
    itemsByAreaKey.set(item.areaKey, areaItems);
  }

  const payload = {
    source: 'vfloow',
    intent:
      'Build this Portal proposal from the exact bill of materials below. Use the provided Portal catalog ReferencedItemId when present. Do not substitute a different product when ReferencedItemId is present. Keep each item in its listed area. Do not create internal notes from this source content.',
    proposal: {
      name: proposalName,
      projectName: preview.project.name,
      clientText: preview.project.client,
      locationText: preview.project.location,
    },
    areas: Array.from(itemsByAreaKey.entries()).map(([areaKey, areaItems]) => {
      const createdArea = createdAreaByKey.get(areaKey);
      return {
        areaName: createdArea?.areaName || areaItems[0]?.areaName || 'vfloow Area',
        portalAreaId: createdArea?.portalAreaId || null,
        portalOptionId: createdArea?.portalOptionId || null,
        optionInstruction: createdArea?.portalOptionId
          ? `Place these line items inside Portal area option ${createdArea.portalOptionId}.`
          : 'Place these line items inside the default Draft option for this area.',
        items: areaItems.map(item => ({
          quantity: item.quantity,
          name: item.displayName,
          status: item.status,
          portalAreaId: createdArea?.portalAreaId || null,
          portalOptionId: createdArea?.portalOptionId || null,
          portalIdentity: getPortalIdentity(item),
          referencedItemId: item.product?.referencedItemId || null,
          portalItemId: item.product?.portalItemId || null,
          portalItemIdentifier: item.product?.portalItemIdentifier || null,
          brand: item.product?.brand || null,
          model: item.product?.model || null,
          partNumber: item.product?.partNumber || null,
          description: item.product?.description || null,
          category: item.product?.category || item.entityType,
          subcategory: item.product?.subcategory || null,
          partDetail: getPartDetail(item),
        })),
      };
    }),
  };

  return [
    'vfloow structured bill of materials for Portal AI Builder',
    '',
    'Instructions:',
    '- Create real proposal line items from this bill of materials.',
    '- Use the exact Portal catalog item when referencedItemId is present.',
    '- Keep items grouped under the matching Portal area and Portal option ID when provided.',
    '- Do not create extra areas or options when a portalAreaId or portalOptionId is provided.',
    '- Do not create proposal internal notes or area internal notes from this file.',
    '',
    JSON.stringify(payload, null, 2),
  ].join('\n');
}

async function waitForCompletedOutline(
  client: ReturnType<typeof createPortalioClient>,
  proposalId: number,
): Promise<{ status: string | null; completed: boolean }> {
  let lastStatus: string | null = null;
  let lastError: string | null = null;
  const deadline = Date.now() + 60000;

  while (Date.now() < deadline) {
    await wait(3000);
    try {
      const outline = await client.getAiOutline(proposalId);
      lastStatus = typeof outline?.status === 'string' ? outline.status : null;
      lastError = null;

      if (lastStatus?.toLowerCase() === 'completed') {
        return { status: lastStatus, completed: true };
      }
    } catch (error) {
      const message = getErrorMessage(error);
      lastError = message;
      if (!message.includes('status 404')) {
        throw error;
      }
    }
  }

  return { status: lastStatus || lastError, completed: false };
}

async function waitForAiContentProcessing(
  client: ReturnType<typeof createPortalioClient>,
  proposalId: number,
  contentId: number | null,
): Promise<{
  status: string | null;
  ready: boolean;
  contentCount: number;
  statuses: string[];
}> {
  let lastStatus: string | null = null;
  let lastContentCount = 0;
  let lastStatuses: string[] = [];
  const deadline = Date.now() + 180000;

  while (Date.now() < deadline) {
    await wait(3000);
    const response = await client.listAiBuilderContent(proposalId);
    const contentItems = Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response)
          ? response
          : [];
    lastContentCount = contentItems.length;
    lastStatuses = contentItems
      .map((content: any) => (typeof content?.status === 'string' ? content.status : null))
      .filter((status: string | null): status is string => Boolean(status));
    const matchedContent =
      contentId === null
        ? contentItems[0]
        : contentItems.find((content: any) => Number(content?.id) === contentId);

    lastStatus = typeof matchedContent?.status === 'string' ? matchedContent.status : lastStatus;
    const normalizedStatus = lastStatus?.toLowerCase();

    if (normalizedStatus === 'completed' || normalizedStatus === 'transcribed') {
      return {
        status: lastStatus,
        ready: true,
        contentCount: lastContentCount,
        statuses: lastStatuses,
      };
    }

    if (normalizedStatus === 'invalid' || normalizedStatus === 'failed') {
      return {
        status: lastStatus,
        ready: false,
        contentCount: lastContentCount,
        statuses: lastStatuses,
      };
    }
  }

  return {
    status: lastStatus,
    ready: false,
    contentCount: lastContentCount,
    statuses: lastStatuses,
  };
}

async function resolveAreaOptions(
  client: ReturnType<typeof createPortalioClient>,
  proposalId: number,
  createdAreas: PortalOutboundCreatedArea[],
): Promise<void> {
  const proposal = await client.getProposalRaw(proposalId);

  for (const createdArea of createdAreas) {
    const refreshedArea = findProposalArea(
      proposal,
      createdArea.areaName,
      createdArea.portalAreaId,
    );
    const refreshedAreaId = Number(refreshedArea?.id);

    if (Number.isFinite(refreshedAreaId) && refreshedAreaId > 0) {
      createdArea.portalAreaId = refreshedAreaId;
    }

    createdArea.portalOptionId = getDefaultOptionId(refreshedArea);

    if (!createdArea.portalOptionId && createdArea.portalAreaId > 0) {
      const optionResponse = await client.addProposalAreaOption(
        proposalId,
        createdArea.portalAreaId,
      );
      const updatedArea = findProposalArea(
        optionResponse,
        createdArea.areaName,
        createdArea.portalAreaId,
      );
      createdArea.portalOptionId = getDefaultOptionId(updatedArea);
    }
  }
}

async function waitForBuiltProposalLineItems(
  client: ReturnType<typeof createPortalioClient>,
  proposalId: number,
): Promise<{ itemCount: number; foundItems: boolean }> {
  let itemCount = 0;
  const deadline = Date.now() + 90000;

  while (Date.now() < deadline) {
    await wait(5000);
    const proposal = await client.getProposalRaw(proposalId);
    itemCount = countProposalLineItems(proposal);

    if (itemCount > 0) {
      return { itemCount, foundItems: true };
    }
  }

  return { itemCount, foundItems: false };
}

export class PortalOutboundCreateService {
  private previewService = createPortalOutboundPreviewService();

  async getStoredPortalUser(orgId: string): Promise<PortalOutboundPortalUser | null> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('portal_integrations')
      .select('portal_user_id, portal_user_name, portal_user_email')
      .eq('clerk_org_id', orgId)
      .eq('is_active', true)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    const id = Number(data.portal_user_id);
    if (!Number.isInteger(id) || id <= 0) {
      return null;
    }

    return {
      id,
      firstName: compactText(data.portal_user_name),
      lastName: null,
      email: compactText(data.portal_user_email),
      permissionGroup: 'Connected Portal user',
    };
  }

  async listPortalUsers(orgId: string): Promise<PortalOutboundPortalUser[]> {
    const client = createPortalioClient(orgId);
    return client.getUsers();
  }

  async createProposalFromFloorPlan(
    sourceFloorPlanId: string,
    input: PortalOutboundCreateInput,
  ): Promise<PortalOutboundCreateResult> {
    const proposalName = compactText(input.proposalName);

    if (!proposalName) {
      throw new Error('Proposal name is required');
    }

    if (!input.orgId) {
      throw new Error('Portal organization is required');
    }

    if (!Number.isFinite(input.salesPersonId)) {
      throw new Error('Portal salesperson is required');
    }

    const preview = await this.previewService.buildPreview(sourceFloorPlanId);
    const creatableItems = preview.items.filter(item => item.status !== 'blocked');

    if (creatableItems.length === 0) {
      throw new Error('At least one product is required before creating a proposal');
    }

    if (preview.counts.blocked > 0) {
      throw new Error('Resolve blocked items before creating a Portal proposal');
    }

    const client = createPortalioClient(input.orgId);
    const proposal = await client.createProposal({
      salesPersonId: input.salesPersonId,
      name: proposalName,
    });

    const proposalId = Number(proposal?.id);
    if (!Number.isFinite(proposalId)) {
      throw new Error('Portal did not return a proposal ID');
    }

    if (preview.project.description) {
      await client.updateProposalDescription(proposalId, preview.project.description);
    }

    const itemsByAreaKey = new Map<string, PortalOutboundPreviewItem[]>();
    for (const item of creatableItems) {
      const areaItems = itemsByAreaKey.get(item.areaKey) || [];
      areaItems.push(item);
      itemsByAreaKey.set(item.areaKey, areaItems);
    }

    const areaNames = makeUniqueAreaNames(preview);
    const createdAreas: PortalOutboundCreatedArea[] = [];

    for (const [areaKey, areaItems] of itemsByAreaKey.entries()) {
      const areaName = areaNames.get(areaKey) || areaItems[0]?.areaName || 'vfloow Area';
      const areaResponse = await client.addProposalArea(proposalId, areaName);
      const createdArea = findCreatedArea(areaResponse, areaName);
      const portalAreaId = Number(createdArea?.id);
      const portalOptionId = getDefaultOptionId(createdArea);

      createdAreas.push({
        areaKey,
        areaName,
        portalAreaId: Number.isFinite(portalAreaId) ? portalAreaId : 0,
        portalOptionId,
        exportedItems: areaItems.reduce((total, item) => total + item.quantity, 0),
      });
    }

    await resolveAreaOptions(client, proposalId, createdAreas);

    const aiContentName = `${proposalName} vfloow BOM`;
    const aiBuilder: PortalOutboundCreateResult['aiBuilder'] = {
      contentName: aiContentName,
      uploadedContentId: null,
      uploadStatus: null,
      outlineStatus: null,
      buildStatus: null,
      buildQueued: false,
      observedLineItems: null,
      warning: null,
    };

    try {
      const aiContent = buildAiBuilderContent(preview, proposalName, creatableItems, createdAreas);
      const uploadedContent = await client.uploadAiBuilderTextContent(
        proposalId,
        aiContentName,
        aiContent,
      );
      aiBuilder.uploadedContentId = Number.isFinite(Number(uploadedContent?.id))
        ? Number(uploadedContent.id)
        : null;
      aiBuilder.uploadStatus =
        typeof uploadedContent?.status === 'string' ? uploadedContent.status : null;

      const contentProcessing = await waitForAiContentProcessing(
        client,
        proposalId,
        aiBuilder.uploadedContentId,
      );
      aiBuilder.uploadStatus = contentProcessing.status || aiBuilder.uploadStatus;

      if (!contentProcessing.ready) {
        const statuses = contentProcessing.statuses.length
          ? contentProcessing.statuses.join(', ')
          : 'none';
        aiBuilder.warning = `Portal AI content was not ready for outline generation after 180 seconds. List-content returned ${contentProcessing.contentCount} item(s); statuses: ${statuses}. Portal requires uploaded content to reach Transcribed/Completed before generating an outline.`;
      } else {
        const outlineRequest = await client.generateAiOutline(proposalId);
        aiBuilder.outlineStatus =
          typeof outlineRequest?.status === 'string' ? outlineRequest.status : null;

        const outlineResult = await waitForCompletedOutline(client, proposalId);
        aiBuilder.outlineStatus = outlineResult.status || aiBuilder.outlineStatus;

        if (outlineResult.completed) {
          const buildRequest = await client.buildAiProposal(proposalId);
          aiBuilder.buildStatus =
            typeof buildRequest?.status === 'string' ? buildRequest.status : null;
          aiBuilder.buildQueued = true;

          const buildResult = await waitForBuiltProposalLineItems(client, proposalId);
          aiBuilder.observedLineItems = buildResult.itemCount;

          if (!buildResult.foundItems) {
            aiBuilder.warning =
              'AI Builder was queued, but no Portal line items appeared within 90 seconds. The draft, areas, options, and AI content were created; check the Portal AI Builder result or retry from Portal.';
          }
        } else {
          aiBuilder.warning =
            'AI outline did not complete within 60 seconds. The draft and AI content were created; retry build from Portal or refresh later.';
        }
      }
    } catch (error) {
      aiBuilder.warning = getErrorMessage(error);
    }

    const result: PortalOutboundCreateResult = {
      proposalId,
      proposalName,
      createdAreas,
      exportedItems: creatableItems.reduce((total, item) => total + item.quantity, 0),
      skippedAiFallback: 0,
      skippedBlocked: preview.counts.blocked,
      aiBuilder,
    };

    return result;
  }
}

export function createPortalOutboundCreateService() {
  return new PortalOutboundCreateService();
}
